import React, { useState } from 'react';
import {
  Box,
  Stack,
  useTheme,
  useMediaQuery,
  Popover,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  LocalShipping as EquipmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../../../components/inputs';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';

interface FiltersSectionProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  countryFilter: string | string[];
  setCountryFilter: (country: string | string[]) => void;
  equipmentFilter: string | string[];
  setEquipmentFilter: (equipment: string | string[]) => void;
  countries: string[];
  equipmentTypes: string[];
}

interface FilterOption {
  label: string;
  value: string;
  color?: string;
}

interface FilterDropdownProps {
  label: string;
  icon: React.ReactNode;
  options: FilterOption[];
  selected: string | string[];
  onChange: (value: string | string[]) => void;
  multiSelect?: boolean;
  searchable?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  icon,
  options,
  selected,
  onChange,
  multiSelect = false,
  searchable = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const { t } = useTranslation();

  const open = Boolean(anchorEl);
  const selectedArray = Array.isArray(selected) ? selected : (selected && selected !== 'all' ? [selected] : []);
  const selectedCount = selectedArray.length;

  const filteredOptions = searchable && searchQuery
    ? options.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleToggle = (value: string) => {
    if (multiSelect) {
      const currentSelected = Array.isArray(selected) ? selected : [];
      if (currentSelected.includes(value)) {
        onChange(currentSelected.filter(v => v !== value));
      } else {
        onChange([...currentSelected, value]);
      }
    } else {
      onChange(value);
      handleClose();
    }
  };

  const getDisplayText = () => {
    if (multiSelect) {
      if (selectedCount === 0) return label;
      if (selectedCount === 1) {
        const option = options.find(opt => opt.value === selectedArray[0]);
        return option?.label || label;
      }
      return `${selectedCount} ${t('selected')}`;
    } else {
      if (!selected || selected === 'all') return label;
      const option = options.find(opt => opt.value === selected);
      return option?.label || label;
    }
  };


  return (
    <>
      <Box
        component="button"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          border: '1px solid',
          borderColor: open || selectedCount > 0
            ? theme.palette.text.purple
            : theme.palette.border.default,
          borderRadius: { xs: '10px', sm: '12px' },
          backgroundColor: selectedCount > 0
            ? theme.palette.background.purpleLight
            : theme.palette.background.white,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: open
            ? (theme.palette.mode === 'dark'
              ? '0 0 0 2px rgba(124, 58, 237, 0.30)'
              : '0 0 0 2px rgba(124, 58, 237, 0.20)')
            : 'none',
          '&:hover': {
            backgroundColor: selectedCount > 0
              ? theme.palette.background.purpleMedium
              : theme.palette.background.lightGrey,
            borderColor: open || selectedCount > 0
              ? theme.palette.text.purple
              : theme.palette.border.medium,
          },
          minWidth: { xs: '100%', sm: 120, md: 130 },
          maxWidth: { xs: '100%', sm: 180, md: 'none' },
          flex: { xs: '1 1 100%', sm: '0 1 auto', md: '0 1 auto' },
          width: { xs: '100%', sm: 'auto', md: 'auto' },
        }}
      >
        <Box sx={{
          color: 'text.dark',
          display: 'flex',
          alignItems: 'center'
        }}>
          {icon}
        </Box>
        <Typography
          sx={{
            flex: 1,
            fontSize: (theme as any).typography.fontSizes.body,
            fontWeight: 400,
            color: 'text.dark',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {getDisplayText()}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 20,
            color: 'text.dark',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: { xs: 'calc(100vw - 32px)', sm: 280 },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 320, lg: 360 },
            maxHeight: { xs: '60vh', sm: 400 },
            borderRadius: { xs: '10px', sm: '12px' },
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            border: '1px solid',
            borderColor: 'border.default',
            backgroundColor: 'background.white',
            overflow: 'hidden',
          }
        }}
      >
        <Box>
          {searchable && (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder={t('Search') + '...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{
                        fontSize: 18,
                        color: 'text.dark'
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon sx={{
                          fontSize: 16,
                          color: 'text.dark'
                        }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    fontSize: (theme as any).typography.fontSizes.body,
                    backgroundColor: 'background.white',
                    border: '1px solid',
                    borderColor: 'border.default',
                    borderRadius: { xs: '10px', sm: '12px' },
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      backgroundColor: 'background.lightGrey',
                      borderColor: 'border.medium',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'background.white',
                      borderColor: 'text.purple',
                      boxShadow: `0 0 0 2px ${theme.palette.border.purple}`,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.dark',
                    '&::placeholder': {
                      color: 'text.grey',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Divider sx={{ mb: 1 }} />
            </>
          )}

          <Box sx={{
            maxHeight: { xs: 'calc(60vh - 120px)', sm: 300 },
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'background.grey',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'border.medium',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'border.dark',
              },
            },
          }}>
            {filteredOptions.length === 0 ? (
              <Typography sx={{ p: 2, textAlign: 'center', color: 'text.grey', fontSize: (theme as any).typography.fontSizes.body }}>
                {t('No options found')}
              </Typography>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = multiSelect
                  ? selectedArray.includes(option.value)
                  : selected === option.value;
                const color = option.color;

                return (
                  <Box
                    key={option.value}
                    onClick={() => handleToggle(option.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      backgroundColor: isSelected
                        ? 'background.lightGrey'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: 'background.lightGrey',
                      },
                    }}
                  >
                    {multiSelect ? (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          borderRadius: '4px',
                          border: '1.5px solid',
                          borderColor: isSelected
                            ? theme.palette.success.main
                            : 'border.medium',
                          backgroundColor: isSelected
                            ? theme.palette.success.main
                            : 'transparent',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {isSelected && (
                          <CheckIcon
                            sx={{
                              fontSize: 14,
                              color: 'text.white',
                              fontWeight: 700
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: isSelected ? (color || theme.palette.text.purple) : 'border.default',
                          backgroundColor: isSelected ? (color || theme.palette.text.purple) : 'transparent',
                          mr: 1.5,
                          transition: 'all 0.2s ease',
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: (theme as any).typography.fontSizes.body,
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected
                          ? 'text.purple'
                          : 'text.dark',
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const FiltersSection: React.FC<FiltersSectionProps> = ({
  searchTerm,
  setSearchTerm,
  countryFilter,
  setCountryFilter,
  equipmentFilter,
  setEquipmentFilter,
  countries,
  equipmentTypes
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatEquipmentLabel = (equipment: string): string => equipment
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());

  const countryOptions = countries.map(countryCode => ({
    label: COUNTRY_NAMES[countryCode as CountryCode] || countryCode,
    value: countryCode
  }));

  const equipmentOptions = equipmentTypes.map(equipment => ({
    label: formatEquipmentLabel(equipment),
    value: equipment
  }));

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1.5}
      sx={{
        width: '100%',
        mb: 2,
        mt: { xs: 2, sm: 2.5, md: 5 },
        flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'nowrap' }
      }}
    >
      {/* Search Input - Longer width */}
      <Box sx={{
        flex: { xs: '1 1 100%', sm: '1 1 auto', md: '1 1 300px', lg: '1 1 350px' },
        minWidth: { xs: '100%', sm: 200, md: 250, lg: 300 },
        maxWidth: { xs: '100%', sm: 'none', md: 400, lg: 450 }
      }}>
        <SearchInput
          placeholder={t('Search trucks, plates, drivers...')}
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </Box>

      {/* Filter Dropdowns - Stack vertically on mobile, horizontal on larger screens */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1.5}
        sx={{
          flex: { xs: '1 1 100%', sm: '1 1 auto' },
          width: { xs: '100%', sm: 'auto' },
          minWidth: 0
        }}
      >
        {/* Country Filter */}
        {countries.length > 0 && (
          <Box sx={{
            width: { xs: '100%', sm: 'auto' },
            flex: { xs: '0 0 auto', sm: '0 1 auto', md: '0 0 auto' },
            minWidth: { xs: '100%', sm: 120, md: 130 },
            maxWidth: { xs: '100%', sm: 180, md: 160 }
          }}>
            <FilterDropdown
              label={t('Country')}
              icon={<LocationIcon sx={{ fontSize: 18 }} />}
              options={countryOptions}
              selected={countryFilter}
              onChange={setCountryFilter}
              multiSelect={true}
              searchable={true}
            />
          </Box>
        )}

        {/* Equipment Filter */}
        {equipmentTypes.length > 0 && (
          <Box sx={{
            width: { xs: '100%', sm: 'auto' },
            flex: { xs: '0 0 auto', sm: '1 1 auto', md: '1 1 auto' },
            minWidth: { xs: '100%', sm: 140, md: 160 },
            maxWidth: { xs: '100%', sm: 'none', md: 'none' }
          }}>
            <FilterDropdown
              label={t('Equipment')}
              icon={<EquipmentIcon sx={{ fontSize: 18 }} />}
              options={equipmentOptions}
              selected={equipmentFilter}
              onChange={setEquipmentFilter}
              multiSelect={true}
              searchable={true}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default FiltersSection;
